/**
 * heroConversionGate — política unificada de supresión de hero CTA por
 * señales server (Worker) + cliente (iplocate via /api/cta-config-snapshot).
 *
 * AR es el target principal: el Worker corre `resolveArgentinaGeo` con
 * iplocate (Capa 2) y emite confidence/privacy. Para non-AR, las señales
 * efectivas son `hideCta` (VPN provider + timezone mismatch).
 *
 * Política tiered (orden de prioridad descendente — el primer match gana):
 *
 *   1. bot / datacenter (iplocate isHosting=true o geo.confidence="bot")
 *      → ocultar paid + free. Tráfico no-humano, no aporta data.
 *
 *   2. hideCta=true del Worker (VPN provider o timezone mismatch)
 *      → ocultar paid + free. Humano pero alto riesgo de fraude.
 *
 *   3. anonymous (iplocate isAnonymous=true o geo.confidence="anonymous")
 *      → ocultar solo paid. VPN/Tor/iCloud Relay: humano real con privacy,
 *      no convierte a pago pero el free Telegram es valor sin riesgo.
 *
 *   4. ok → mostrar todo.
 *
 * Por qué tiered y no binario: la cascada AR distingue 3 niveles con cache
 * TTLs distintos (5min bot / 10min anon / 1h normal). Tratar todo como
 * binario desperdicia esa granularidad y bloquea conversiones del free
 * para humanos privacy-conscious.
 */

import type { LiveCTAState } from "@/hooks/useLiveCTA";
import type { ServerTemplateConfig } from "@/lib/serverTemplateDetection";

export type HeroGateReason = "ok" | "bot" | "hideCta" | "anonymous";

export interface HeroConversionGate {
  /** Ocultar el botón paid (VIP / membership / Stripe / MP checkout). */
  readonly hidePaid: boolean;
  /** Ocultar el botón free (Telegram / acceso gratis). */
  readonly hideFree: boolean;
  /** Razón machine-readable — usar para analytics y debugging. */
  readonly reason: HeroGateReason;
}

const OK: HeroConversionGate = Object.freeze({
  hidePaid: false,
  hideFree: false,
  reason: "ok",
});

export function heroConversionGate(
  cfg: Pick<ServerTemplateConfig, "hideCta"> | null | undefined,
  liveCTA: LiveCTAState | null | undefined,
): HeroConversionGate {
  // 1. Datacenter / scraper / hosting IP → ocultar todo el funnel.
  //    isHosting solo viene poblado cuando la detección pasó por iplocate
  //    (AR Capa 2). geo.confidence="bot" lo respaldea.
  if (
    liveCTA?.geo?.confidence === "bot" ||
    liveCTA?.privacy?.isHosting === true
  ) {
    return { hidePaid: true, hideFree: true, reason: "bot" };
  }

  // 2. Worker emitió hideCta=true → VPN provider explícito o timezone
  //    mismatch. Humano con riesgo de fraude → ocultar conversión.
  if (cfg?.hideCta === true) {
    return { hidePaid: true, hideFree: true, reason: "hideCta" };
  }

  // 3. Anónimo (VPN/Tor/iCloud Relay sin datacenter): humano real con
  //    privacy. Ocultar paid (no convierte) pero mantener free Telegram
  //    (valor sin riesgo, conserva engagement).
  if (
    liveCTA?.geo?.confidence === "anonymous" ||
    liveCTA?.privacy?.isAnonymous === true
  ) {
    return { hidePaid: true, hideFree: false, reason: "anonymous" };
  }

  return OK;
}
